import { useState, useRef } from "react";

import { AttachedFile } from "modules/gallery";

type UseTinyMceAuthoringLogicReturnType = {
    inlineImageDialogOpen: boolean;
    imageGalleryDialogOpen: boolean;
    intenseEmphasisDialogOpen: boolean;
    editorRef: React.MutableRefObject<any>; // can't get typing for this object
    onShowInlineImageDialog: () => void;
    onShowImageGalleryDialog: () => void;
    onShowIntenseEmphasisDialog: () => void;
    onApplyInlineImage: (images: AttachedFile[], maxWidth?: number) => Promise<void>;
    onApplyIntenseEmphasis: (color: string) => void;
    onHideInlineImageDialog: () => void;
    onHideImageGalleryDialog: () => void;
    onHideIntenseEmphasisDialog: () => void;
}

/**
 * Encapsulates common logic when using authoringTinyMce
 */
const useTinyMceAuthoringLogic = (getImage: (id: string) => Promise<AttachedFile> ): UseTinyMceAuthoringLogicReturnType => {
    const [inlineImageDialogOpen, setInlineImageDialogOpen] = useState<boolean>(false);
    const [imageGalleryDialogOpen, setImageGalleryDialogOpen] = useState<boolean>(false);
    const [intenseEmphasisDialogOpen, setIntenseEmphasisDialogOpen] = useState<boolean>(false);

    const editorRef = useRef<any>(null);

    const onHideInlineImageDialog = () => setInlineImageDialogOpen(false);
    const onHideImageGalleryDialog = () => setImageGalleryDialogOpen(false);
    const onHideIntenseEmphasisDialog = () => setIntenseEmphasisDialogOpen(false);

    const onShowInlineImageDialog = () => setInlineImageDialogOpen(true);
    const onShowImageGalleryDialog = () => setImageGalleryDialogOpen(true);
    const onShowIntenseEmphasisDialog = () => setIntenseEmphasisDialogOpen(true);

    const onChangeInlineImage = (image: AttachedFile, maxWidth?: number) => {
        if (editorRef.current)
            editorRef.current.insertContent(`<img src="${image.fileUrl}" data-id="${image.ospreyId}" ${maxWidth ? `max-width="${maxWidth}"` : ""}/>`);
    }

    const onApplyInlineImage = async (images: AttachedFile[], maxWidth?: number) => {
        try {
            const img = await getImage(images[0].ospreyId);
            onChangeInlineImage(img, maxWidth);
        } catch (error) {
            onChangeInlineImage(images[0], maxWidth);
        } finally {
            onHideInlineImageDialog();
        }
    }

    const onChangeIntenseEmphasis = (color: string) => {
        if (editorRef.current)
            editorRef.current.formatter.apply("intense-emphasis", { color });
    }

    const onApplyIntenseEmphasis = (color: string) => {
        onChangeIntenseEmphasis(color);
        onHideIntenseEmphasisDialog();
    }

    return {
        inlineImageDialogOpen,
        imageGalleryDialogOpen,
        intenseEmphasisDialogOpen,
        editorRef,
        onApplyInlineImage,
        onShowInlineImageDialog,
        onShowImageGalleryDialog,
        onShowIntenseEmphasisDialog,
        onApplyIntenseEmphasis,
        onHideInlineImageDialog,
        onHideImageGalleryDialog,
        onHideIntenseEmphasisDialog,
    };
}

export { useTinyMceAuthoringLogic };
